import React, { useEffect, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useMergeState } from '../../../../irmlib/src/hooks'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import * as routePaths from '../../../../navigation/routePaths'
import * as constants from '../../../../irmlib/src/common/constants'
import * as gdeTransactions from '../../../../services/firestore/gdeTransactions'
import { closeGeneralDialog, openGeneralDialog, clearBusy, setBusy } from '../../../../irmlib/src/containers'
import { ContainedButton, OutlinedButton } from '../../../../irmlib/src/components/Controls'
import { successToast, errorToast } from '../../../../common/functions'
import { getFunctions, httpsCallable } from '../../../../utils/firestore'
import { userRoleAdmin, userRoleSuperUser, userRoleUser } from '../../../../common/constants'
import { FlexByRow, XDivider } from 'irmlib/styles'
import UserRolePermission from '../../../Controls/UserRolePermission'
import PropTypes from 'prop-types'

const ButtonsContainer = styled(FlexByRow)`
  justify-content: flex-end;
`

const initialState = {
  readyToTransmitCheck: false,
  transmitButton: false,
  jobsforTransmission: [],
  allJobsforTransmission: ''
}

const TopButtons = forwardRef((props, ref) => {
  const [, setState, getState] = useMergeState(initialState)
  const history = useHistory()
  const { t } = useTranslation()
  const selectedRows = props.selectedData
  const [disabledTransmit, setdisabledTransmit] = React.useState(true)
  const [disabledRetransmit, setReTransmitJobId] = React.useState(true)
  const [allowManualTransmit, setAllowManualTransmit] = React.useState(false)
  const functions = getFunctions()
  
  useEffect(() => {
    setdisabledTransmit(true)
    // when there is only one job selected for transmission
    if (selectedRows?.length === 1) {
      if (selectedRows[0].status === constants.jobStatus.readyToTransmit && selectedRows[0].manualTransmit === 'true') {
        onTransmitSelection(selectedRows[0])
          .then(result => {
            result
              ? setdisabledTransmit(false) 
              : setdisabledTransmit(true)
          })
      } else if (selectedRows[0].status === constants.jobStatus.transmitted) {
        setReTransmitJobId(false)
      } else {
        setdisabledTransmit(true)
        setReTransmitJobId(true)
      }
    } else if (selectedRows?.length > 1) {
      setReTransmitJobId(true)
      // when there are multiple jobs selected for transmission
      handleTransmission(selectedRows)
    } else {
      setdisabledTransmit(true)
      setReTransmitJobId(true)
    }
  }, [props.selectedData])

  useEffect(() => {
    const additionalGivenPermissions = window?.sessionStorage?.getItem('additional-permissions-user')
    if (additionalGivenPermissions !== 'undefined' && additionalGivenPermissions !== undefined && userRoleUser) {
      const additionalPermissionsForUser = JSON.parse(additionalGivenPermissions)
      const indexOfManuallTransmitJob = additionalPermissionsForUser?.map(object => object?.value)?.indexOf('manuallyTransmitJob')

      if (additionalPermissionsForUser[indexOfManuallTransmitJob]?.enabled) {
        setAllowManualTransmit(userRoleUser)
      }                              
    }
  }, [])
  
  const onTransmitSelection = async rows => {
    const collection = await gdeTransactions.getBoxesForJob(rows.jobId)
    const boxList = []
    const boxUserList = []
  
    collection.forEach((doc) => {
      const data = doc.data()
      boxList.push({
        boxId: data.boxId,
        entryUser: data.dataEntryUser,
        status: data.status,
        dataEntryUser: data.dataEntryUser,
        verificationUser: data.verificationUser
      })
      boxUserList.push(doc.data().dataEntryUser)
    })
    const checkReadyToTransmit = (Boxes) => {
      return Boxes.status === constants.boxStatus.approved || Boxes.status === constants.boxStatus.exception
    }
    const result = boxList.filter(checkReadyToTransmit)
  
    const transmitBoxEqual = boxList.length === 0 ? false : rows.boxes === result.length
    setState({ jobsforTransmission: rows })
    if (transmitBoxEqual) {
      return true
    } else {
      return false
    }
  }

  const onMultipleTransmitSelection = async rows => {
    if (rows?.length > 10) {
      const ten = 10
      errorToast({ message: t('messages.AlertText', { count: ten }), title: t('messages.attention'), isBlocking: true, closeIcon: true })
    } else {
      setBusy()
      let multiplejobs = ''
      // Checking that all of the selected jobs have the necessary box count for transmission.
      for (let i = 0; i < rows?.length; i++) {
        const collection = await gdeTransactions.getBoxesForJob(rows[i].jobId)
        const boxList = []
        const boxUserList = []
        collection.forEach((doc) => {
          const data = doc.data()
          boxList.push({
            boxId: data.boxId,
            entryUser: data.dataEntryUser,
            status: data.status,
            dataEntryUser: data.dataEntryUser,
            verificationUser: data.verificationUser
          })
          boxUserList.push(doc.data().dataEntryUser)
        })
        const checkReadyToTransmit = (Boxes) => {
          return Boxes.status === constants.boxStatus.approved || Boxes.status === constants.boxStatus.exception
        }
        const result = boxList.filter(checkReadyToTransmit)
      
        const transmitBoxEqual = boxList.length === 0 ? false : rows[i].boxes === result.length
        
        if (transmitBoxEqual) {
          // setting a state variable to verify if all the boxCounts of the selected jobs are accurate for transmission
          setState({ transmitButton: true })
          multiplejobs += `${rows[i].jobId}, `
          // Storing the job Ids in a string for displaying in the message
          setState({ allJobsforTransmission: multiplejobs })
        } else {
          // if any of the boxCounts of the selected jobs are not accurate for transmission
          setState({ transmitButton: false })
          break
        }
      }
      clearBusy()
      // setting the result as true or false based on the transmit button state variable for Transmit button enabling
      if (getState().transmitButton) {
        // storing the jobs if All the conditions for transmission are passed 
        setState({ jobsforTransmission: rows })
        return true
      } else {
        return false
      }
    }
  }
  
  const handleTransmit = () => {
    // when there are multiple jobs selected for transmission
    if (getState().jobsforTransmission?.length > 1) {
      openGeneralDialog({
        title: t('messages.multipleTransmit'),
        message: t('messages.multipleTransmitConfirmMessage', { jobList: getState().allJobsforTransmission }),
        translateMessage: false,
        noTranslateTitle: true,
        buttons: <ButtonsForTransmitDialogBox />,
        closeX: true
      })
    } else {
      // when only one job is selected for transmission
      openGeneralDialog({
        title: t('messages.transmit'),
        message: t('messages.transmitConfirmMessage'),
        translateMessage: false,
        noTranslateTitle: true,
        buttons: <ButtonsForTransmitDialogBox />,
        closeX: true
      })
    }
  }

  const handleTransmission = (selectedRows) => {
    // We are verifying that the selected jobs are in the Manual transmit and Ready to transmit status.
    for (let i = 0; i < selectedRows?.length; i++) {
      if (selectedRows[i].status === constants.jobStatus.readyToTransmit && selectedRows[i].manualTransmit === 'true') {
        setState({ readyToTransmitCheck: true })
      } else {
        setState({ readyToTransmitCheck: false })
        break
      }
    }
    if (getState().readyToTransmitCheck) {
      // function for checking if the selected jobs have the correct box count for transmission and enabling the translit button.
      onMultipleTransmitSelection(selectedRows)
        .then(result => {
          result
            ? setdisabledTransmit(false) 
            : setdisabledTransmit(true)
        })
    } else {
      // Disabling the transmit button if the selected jobs are not in Ready to transmit or Manual transmit status. 
      setdisabledTransmit(true) 
    }
  }
  
  const handleReTransmit = () => {
    openGeneralDialog({
      title: t('messages.reTransmit'),
      message: t('messages.reTransmitConfirmMessage'),
      translateMessage: false,
      noTranslateTitle: true,
      buttons: <ButtonsForRetransmitDialogBox />,
      closeX: true
    })
  }
  
  const ButtonsForRetransmitDialogBox = () =>
    <>
      <OutlinedButton onClick={closeGeneralDialog} >buttons.no</OutlinedButton>
      <ContainedButton onClick={handleRetransmitButtonOnClick} >buttons.yes</ContainedButton>
    </>
  
  const handleRetransmitButtonOnClick = async () => {
    const data = {
      actionTs: new Date(),
      boxesCompleted: 0,
      jobLevelRetransmit: true,
      jobStatus: constants.jobStatus.readyToTransmit,
      autoTransmit: 'true',
      autoTransmitAndStatus: `true-${constants.jobStatus.readyToTransmit}`,
    }
    const retransmitMetricData = {
      activityType: constants.retransmitAction,
      jobId: selectedRows[0]?.jobId,
    }
    await gdeTransactions.updateJob(selectedRows[0]?.jobId, data)
  
    const boxesRaw = await gdeTransactions.getBoxesForJob(selectedRows[0]?.jobId)
    boxesRaw.forEach(async(ele) => {
      const box = ele.data()
      await gdeTransactions.updateBoxData(selectedRows[0]?.jobId, box.boxId, { 
        actionTs: new Date(), 
        status: constants.boxStatus.approved,
        transmissionStatus: constants.boxTransmissionStatus.pending,
        transmissionStatusTs: new Date(), 
      })
    })
  
    setReTransmitJobId(true)
    await gdeTransactions.saveMetricsData(retransmitMetricData)
    successToast({ message: t('toast.reTransmitQueued', { jobId: selectedRows[0]?.jobId }) })
    closeGeneralDialog()
  }
  
  const handleTransmitButtonOnClick = () => {
    // when there are multiple jobs for transmission
    if (selectedRows?.length > 1) {
      setdisabledTransmit(true)
      for (let i = 0; i < getState().jobsforTransmission?.length; i++) {
        const callabletransmit = httpsCallable(functions, 'grpGde-ManuallyExportSkpApprovedAssets')
        callabletransmit({ jobId: getState().jobsforTransmission[i].jobId }).then((result) => {
          successToast({ message: t('toast.transmitQueued', { jobId: getState().allJobsforTransmission }) })
        }).catch(error => {
          errorToast({ message: error = t('toast.manualTransmitFailMessage'), title: t('toast.manualTransmitFailTitle') })
          clearBusy()
        })
      }
      closeGeneralDialog()
    } else {
      // when only one job is selected for transmission
      setdisabledTransmit(true)
      const callabletransmit = httpsCallable(functions, 'grpGde-ManuallyExportSkpApprovedAssets')
      callabletransmit({ jobId: selectedRows[0]?.jobId }).then((result) => {
        successToast({ message: t('toast.transmitQueued', { jobId: selectedRows[0]?.jobId }) })
      }).catch(error => {
        errorToast({ message: error = t('toast.manualTransmitFailMessage'), title: t('toast.manualTransmitFailTitle') })
        clearBusy()
      })
      closeGeneralDialog()
    }
    // for unchecking the selected job or jobs for transmisson
    history.push(`${routePaths.myJobs}`)
  }

  const ButtonsForTransmitDialogBox = () =>
    <>
      <OutlinedButton onClick={closeGeneralDialog} >buttons.no</OutlinedButton>
      <ContainedButton onClick={handleTransmitButtonOnClick} >buttons.yes</ContainedButton>
    </>
  return (
    <ButtonsContainer>
      <UserRolePermission allowsTo={[userRoleAdmin, userRoleSuperUser, allowManualTransmit]}>
        <OutlinedButton disabled={disabledTransmit} id={'allJobs_editJobs'} size='small' onClick={handleTransmit} noTranslate>
          {t('demo.Transmit')}
        </OutlinedButton>
        <XDivider width='18px' />
      </UserRolePermission>
      <UserRolePermission allowsTo={[userRoleAdmin, userRoleSuperUser]}>
        <OutlinedButton disabled={disabledRetransmit} id={'allJobs_editJobs'} size='small' onClick={handleReTransmit} noTranslate>
          {t('demo.reTransmit')}
        </OutlinedButton>
        <XDivider width='18px' />
      </UserRolePermission>
    </ButtonsContainer>
  )
})
  
TopButtons.propTypes = {
  selectedData: PropTypes.object,
  // DataSearch: PropTypes.string,
}

export default TopButtons